// Shared value sets for the native-invoice columns that used to live only in
// the render/email jsonb blobs. Used by the Zod schema (request/render DTO),
// the TypeORM entities (Postgres enum columns), and the DTO ⇄ column mapper so
// a single source of truth backs both the wire format and the database enum.

export const NATIVE_INVOICE_ADJUSTMENT_MODES = ["percent", "amount"] as const;
export type NativeInvoiceAdjustmentMode =
    (typeof NATIVE_INVOICE_ADJUSTMENT_MODES)[number];

export const NATIVE_INVOICE_ADJUSTMENT_SIGNS = [
    "positive",
    "negative",
] as const;
export type NativeInvoiceAdjustmentSign =
    (typeof NATIVE_INVOICE_ADJUSTMENT_SIGNS)[number];

export const NATIVE_INVOICE_FONT_FAMILIES = [
    "inter",
    "arial",
    "helvetica",
    "georgia",
    "times",
    "courier",
] as const;
export type NativeInvoiceFontFamily =
    (typeof NATIVE_INVOICE_FONT_FAMILIES)[number];

export const NATIVE_INVOICE_FOOTER_ALIGNS = [
    "left",
    "center",
    "right",
] as const;
export type NativeInvoiceFooterAlign =
    (typeof NATIVE_INVOICE_FOOTER_ALIGNS)[number];

export const NATIVE_INVOICE_PAGE_SIZES = ["letter", "a4"] as const;
export type NativeInvoicePageSize = (typeof NATIVE_INVOICE_PAGE_SIZES)[number];

// Matches Puppeteer's previous hardcoded native-invoice format so existing
// invoices keep the same page box after this field is persisted.
export const NATIVE_INVOICE_DEFAULT_PAGE_SIZE: NativeInvoicePageSize = "a4";
export const NATIVE_INVOICE_DEFAULT_SHOW_PAGINATION = true;

export const NATIVE_INVOICE_PAGE_SIZE_LABELS: Record<
    NativeInvoicePageSize,
    string
> = {
    letter: "US Letter",
    a4: "A4",
};

export const NATIVE_INVOICE_PAGE_SIZE_DIMENSIONS: Record<
    NativeInvoicePageSize,
    string
> = {
    letter: "8.5 × 11 in",
    a4: "210 × 297 mm",
};

// Physical page box used by the live preview and the PDF renderer. Each
// surface paints discrete pages of this size (48px top + 20px bottom padding
// lives on the page, not on Puppeteer margins).
export const NATIVE_INVOICE_PAGE_SIZE_CSS: Record<
    NativeInvoicePageSize,
    { width: string; height: string }
> = {
    letter: { width: "8.5in", height: "11in" },
    a4: { width: "210mm", height: "297mm" },
};

// Defaults applied on write (§5.2). Tax adds to the total by default; discount
// and deposit subtract. Modes default to a raw dollar amount.
export const NATIVE_INVOICE_DEFAULT_ADJUSTMENT_MODE: NativeInvoiceAdjustmentMode =
    "amount";
export const NATIVE_INVOICE_DEFAULT_TAX_SIGN: NativeInvoiceAdjustmentSign =
    "positive";
export const NATIVE_INVOICE_DEFAULT_DISCOUNT_SIGN: NativeInvoiceAdjustmentSign =
    "negative";
export const NATIVE_INVOICE_DEFAULT_DEPOSIT_SIGN: NativeInvoiceAdjustmentSign =
    "negative";

// User-customizable PDF field labels. Keys are the 24 strings rendered by
// renderNativeInvoiceDocument. Order here
// only affects readability; the label-key enum/schema iterate this array to
// stay a single source of truth for both the wire format and the render.
export const NATIVE_INVOICE_LABEL_KEYS = [
    "invoiceHeading",
    "invoiceNumber",
    "invoiceDate",
    "dueDate",
    "poNumber",
    "termsType",
    "from",
    "to",
    "taxId",
    "item",
    "quantity",
    "rate",
    "amount",
    "taxColumn",
    "discountColumn",
    "depositColumn",
    "subtotal",
    "tax",
    "discount",
    "deposit",
    "totalDue",
    "payThisInvoice",
    "paymentInstructions",
    "memo",
] as const;
export type NativeInvoiceLabelKey = (typeof NATIVE_INVOICE_LABEL_KEYS)[number];

// Current hardcoded strings, kept as the default for every invoice that has
// no override (i.e. every existing invoice, and any new one that doesn't
// touch a given label).
export const NATIVE_INVOICE_LABEL_DEFAULTS: Record<
    NativeInvoiceLabelKey,
    string
> = {
    invoiceHeading: "INVOICE",
    invoiceNumber: "Invoice number",
    invoiceDate: "Invoice date",
    dueDate: "Due date",
    poNumber: "PO number",
    termsType: "Terms",
    from: "From",
    to: "To",
    taxId: "Tax ID",
    item: "Item",
    quantity: "Quantity",
    rate: "Rate",
    amount: "Amount",
    taxColumn: "Tax",
    discountColumn: "Discount",
    depositColumn: "Deposit",
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    deposit: "Deposit",
    totalDue: "Total due",
    payThisInvoice: "Pay this invoice",
    paymentInstructions: "Payment instructions",
    memo: "Memo",
};

export const NATIVE_INVOICE_ADJUSTMENT_KINDS = [
    "tax",
    "discount",
    "deposit",
] as const;
export type NativeInvoiceAdjustmentKind =
    (typeof NATIVE_INVOICE_ADJUSTMENT_KINDS)[number];
